/* PIANZI PRODUCT CAROUSEL V4 */

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-pianzi-product-v4]';
  const WISHLIST_KEY = 'pianziWishlist';
  const DESKTOP_QUERY = '(min-width: 990px)';
  const DRAG_THRESHOLD = 7;

  if (window.PianziProductCarouselV4?.init) {
    window.PianziProductCarouselV4.init(document);
    return;
  }

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const unique = (values) => [...new Set(values)];

  const parseJson = (node) => {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('PIANZI product data could not be read:', error);
      return null;
    }
  };

  const getWishlist = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };
  const wishlistIds = (items = getWishlist()) => items.map((item) => String(typeof item === 'object' ? item.id : item));

  const setWishlist = (items) => {
    const normalized = Array.isArray(items) ? items : [];
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(normalized));
    document.dispatchEvent(new CustomEvent('pianzi:wishlist-change', { detail: { items: normalized } }));
  };

  const shopRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  class PianziProductCarouselV4 {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-ppc-viewport]');
      this.track = root.querySelector('[data-ppc-track]');
      this.slides = Array.from(root.querySelectorAll('[data-ppc-slide]'));
      this.prevButtons = Array.from(root.querySelectorAll('[data-ppc-prev]'));
      this.nextButtons = Array.from(root.querySelectorAll('[data-ppc-next]'));
      this.dotsContainer = root.querySelector('[data-ppc-dots]');
      this.liveRegion = root.querySelector('[data-ppc-live]');
      this.modal = root.querySelector('[data-ppc-modal]');
      this.modalPanel = root.querySelector('.pianzi-product-v4__modal-panel');
      this.modalTitle = root.querySelector('[data-ppc-modal-title]');
      this.modalOptions = root.querySelector('[data-ppc-modal-options]');
      this.modalPrice = root.querySelector('[data-ppc-modal-price]');
      this.modalMessage = root.querySelector('[data-ppc-modal-message]');
      this.modalAdd = root.querySelector('[data-ppc-modal-add]');
      this.modalAddLabel = root.querySelector('[data-ppc-modal-add-label]');
      this.modalDefaultAddLabel = this.modalAddLabel?.textContent.trim() || 'Add to bag';
      this.modalMedia = root.querySelector('[data-ppc-modal-media]');
      this.modalImage = root.querySelector('[data-ppc-modal-image]');

      this.loop = root.dataset.loop === 'true';
      this.autoplay = root.dataset.autoplay === 'true';
      this.autoplaySpeed = Number(root.dataset.autoplaySpeed) || 5000;
      this.pauseOnHover = root.dataset.pauseOnHover === 'true';
      this.desktopDrag = root.dataset.desktopDrag !== 'false';

      this.pagePositions = [0];
      this.currentPage = 0;
      this.dots = [];
      this.scrollFrame = 0;
      this.resizeTimer = 0;
      this.autoplayTimer = 0;
      this.isHoverPaused = false;
      this.isModalOpen = false;
      this.activeCard = null;
      this.activeProduct = null;
      this.activeVariant = null;
      this.selectedOptions = [];
      this.lastFocusedElement = null;
      this.suppressClickUntil = 0;

      this.pointerId = null;
      this.pointerStartX = 0;
      this.pointerStartY = 0;
      this.pointerStartScroll = 0;
      this.isDragging = false;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      if (!this.viewport || !this.track || !this.slides.length) return;

      this.bindCarousel();
      this.bindCards();
      this.bindModal();
      this.measure();
      this.syncWishlist(wishlistIds());
      this.startAutoplay();

      root.pianziProductCarouselV4 = this;
    }

    bindCarousel() {
      this.prevButtons.forEach((button) => {
        button.addEventListener('click', () => this.goPrevious(), { signal: this.signal });
      });

      this.nextButtons.forEach((button) => {
        button.addEventListener('click', () => this.goNext(), { signal: this.signal });
      });

      this.viewport.addEventListener(
        'scroll',
        () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = 0;
            this.updateFromScroll();
          });
        },
        { passive: true, signal: this.signal }
      );

      this.viewport.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.goPrevious();
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.goNext();
          } else if (event.key === 'Home') {
            event.preventDefault();
            this.goToPage(0);
          } else if (event.key === 'End') {
            event.preventDefault();
            this.goToPage(this.pagePositions.length - 1);
          }
        },
        { signal: this.signal }
      );

      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal: this.signal });
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal: this.signal });
      this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal: this.signal });
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event), { signal: this.signal });
      this.viewport.addEventListener(
        'click',
        (event) => {
          if (performance.now() < this.suppressClickUntil) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        { capture: true, signal: this.signal }
      );

      if (this.pauseOnHover) {
        this.root.addEventListener(
          'mouseenter',
          () => {
            this.isHoverPaused = true;
            this.stopAutoplay();
          },
          { signal: this.signal }
        );
        this.root.addEventListener(
          'mouseleave',
          () => {
            this.isHoverPaused = false;
            this.startAutoplay();
          },
          { signal: this.signal }
        );
      }

      window.addEventListener(
        'resize',
        () => {
          window.clearTimeout(this.resizeTimer);
          this.resizeTimer = window.setTimeout(() => this.measure(), 130);
        },
        { passive: true, signal: this.signal }
      );

      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) this.stopAutoplay();
          else this.startAutoplay();
        },
        { signal: this.signal }
      );
    }

    bindCards() {
      this.root.querySelectorAll('[data-ppc-card]').forEach((card) => {
        this.bindSwatches(card);

        const favorite = card.querySelector('[data-ppc-favorite]');
        favorite?.addEventListener(
          'click',
          () => this.toggleFavorite(card, favorite),
          { signal: this.signal }
        );

        const quickAdd = card.querySelector('[data-ppc-quick-add]');
        quickAdd?.addEventListener(
          'click',
          () => this.handleQuickAdd(card, quickAdd),
          { signal: this.signal }
        );
      });

      document.addEventListener(
        'pianzi:wishlist-change',
        (event) => this.syncWishlist(wishlistIds(event.detail?.items || getWishlist())),
        { signal: this.signal }
      );
    }

    bindSwatches(card) {
      const image = card.querySelector('.pianzi-product-v4__image--primary');
      const titleLink = card.querySelector('[data-ppc-title-link]');
      const mediaLink = card.querySelector('[data-ppc-product-link]');
      const cardArrow = card.querySelector('.pianzi-product-v4__card-arrow');
      const price = card.querySelector('[data-ppc-current-price]');
      const comparePrice = card.querySelector('[data-ppc-compare-price]');

      const original = image
        ? {
            src: image.currentSrc || image.src,
            srcset: image.getAttribute('srcset') || ''
          }
        : null;

      const preview = (button, commit = false) => {
        if (!button || button.disabled) return;

        if (commit) {
          card.querySelectorAll('[data-ppc-swatch]').forEach((item) => item.classList.remove('is-active'));
          button.classList.add('is-active');

          const variantUrl = button.dataset.variantUrl;
          if (variantUrl) {
            [titleLink, mediaLink, cardArrow].forEach((link) => {
              if (link?.tagName === 'A') link.href = variantUrl;
            });
          }

          if (price && button.dataset.price) price.textContent = button.dataset.price;
          if (comparePrice) {
            const compare = button.dataset.comparePrice || '';
            comparePrice.textContent = compare;
            comparePrice.hidden = !compare;
          }
        }

        if (image && button.dataset.imageSrc) {
          image.classList.add('is-changing');
          window.setTimeout(() => {
            image.src = button.dataset.imageSrc;
            image.removeAttribute('srcset');
            image.classList.remove('is-changing');
          }, 80);
        }
      };

      card.querySelectorAll('[data-ppc-swatch]').forEach((button) => {
        // A swatch changes the image only after a deliberate selection. Focus/touch
        // no longer previews a color and therefore cannot fight the mobile card state.
        button.addEventListener('pointerdown', (event) => event.stopPropagation(), { signal: this.signal });
        button.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            preview(button, true);
            button.blur();
          },
          { signal: this.signal }
        );
      });
    }

    bindModal() {
      if (!this.modal) return;

      this.modal.querySelectorAll('[data-ppc-modal-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeModal(), { signal: this.signal });
      });

      this.modalAdd?.addEventListener(
        'click',
        () => {
          if (this.activeVariant?.available) {
            this.addVariant(this.activeVariant.id, this.modalAdd, true);
          }
        },
        { signal: this.signal }
      );

      document.addEventListener(
        'keydown',
        (event) => {
          if (!this.isModalOpen) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeModal();
          } else if (event.key === 'Tab') {
            this.trapModalFocus(event);
          }
        },
        { signal: this.signal }
      );
    }

    measure() {
      if (!this.viewport || !this.slides.length) return;

      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const positions = [];

      this.slides.forEach((slide) => {
        const position = clamp(slide.offsetLeft - this.track.offsetLeft, 0, maxScroll);
        if (!positions.some((saved) => Math.abs(saved - position) < 3)) positions.push(position);
      });

      if (!positions.length) positions.push(0);
      if (maxScroll > 2 && !positions.some((saved) => Math.abs(saved - maxScroll) < 3)) positions.push(maxScroll);

      this.pagePositions = positions.sort((a, b) => a - b);
      this.buildDots();
      this.updateFromScroll();
    }

    buildDots() {
      if (!this.dotsContainer) return;

      this.dotsContainer.innerHTML = '';
      this.dots = this.pagePositions.map((position, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pianzi-product-v4__dot';
        button.setAttribute('aria-label', `Go to product page ${index + 1}`);
        button.addEventListener('click', () => this.goToPage(index), { signal: this.signal });
        this.dotsContainer.appendChild(button);
        return button;
      });

      this.dotsContainer.hidden = this.pagePositions.length <= 1;
    }

    nearestPage(scrollLeft = this.viewport.scrollLeft) {
      let nearest = 0;
      let distance = Infinity;

      this.pagePositions.forEach((position, index) => {
        const nextDistance = Math.abs(position - scrollLeft);
        if (nextDistance < distance) {
          distance = nextDistance;
          nearest = index;
        }
      });

      return nearest;
    }

    updateFromScroll() {
      this.currentPage = this.nearestPage();

      this.dots.forEach((dot, index) => {
        const active = index === this.currentPage;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      const atStart = this.currentPage <= 0;
      const atEnd = this.currentPage >= this.pagePositions.length - 1;
      this.prevButtons.forEach((button) => { button.disabled = !this.loop && atStart; });
      this.nextButtons.forEach((button) => { button.disabled = !this.loop && atEnd; });
    }

    goToPage(index, behavior = 'smooth') {
      if (!this.pagePositions.length) return;
      const targetIndex = clamp(index, 0, this.pagePositions.length - 1);
      this.currentPage = targetIndex;
      this.viewport.scrollTo({ left: this.pagePositions[targetIndex], behavior });
      this.updateFromScroll();
      this.restartAutoplay();
      this.announce(`Product page ${targetIndex + 1} of ${this.pagePositions.length}`);
    }

    goPrevious() {
      if (this.currentPage <= 0) {
        if (this.loop) this.goToPage(this.pagePositions.length - 1);
        return;
      }
      this.goToPage(this.currentPage - 1);
    }

    goNext() {
      if (this.currentPage >= this.pagePositions.length - 1) {
        if (this.loop) this.goToPage(0);
        return;
      }
      this.goToPage(this.currentPage + 1);
    }

    onPointerDown(event) {
      if (!this.desktopDrag || !window.matchMedia(DESKTOP_QUERY).matches) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('button, input, select, textarea, label')) return;

      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.pointerStartScroll = this.viewport.scrollLeft;
      this.isDragging = false;
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;

      const deltaX = event.clientX - this.pointerStartX;
      const deltaY = event.clientY - this.pointerStartY;

      if (!this.isDragging) {
        if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this.pointerId = null;
          return;
        }
        this.isDragging = true;
        this.viewport.classList.add('is-dragging');
        this.viewport.setPointerCapture?.(event.pointerId);
        this.stopAutoplay();
      }

      event.preventDefault();
      this.viewport.scrollLeft = this.pointerStartScroll - deltaX;
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return;

      if (this.isDragging) {
        this.suppressClickUntil = performance.now() + 320;
        this.viewport.classList.remove('is-dragging');
        this.viewport.releasePointerCapture?.(event.pointerId);
        this.goToPage(this.nearestPage());
      }

      this.pointerId = null;
      this.isDragging = false;
      this.startAutoplay();
    }

    startAutoplay() {
      if (!this.autoplay || this.pagePositions.length <= 1 || document.hidden || this.isHoverPaused || this.isModalOpen) return;
      this.stopAutoplay();
      this.autoplayTimer = window.setInterval(() => {
        if (this.currentPage >= this.pagePositions.length - 1) {
          if (this.loop) this.goToPage(0);
          else this.stopAutoplay();
        } else {
          this.goToPage(this.currentPage + 1);
        }
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = 0;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    toggleFavorite(card, button) {
      const productId = String(card.closest('[data-product-id]')?.dataset.productId || '');
      if (!productId) return;

      const items = getWishlist();
      const index = items.findIndex((item) => String(typeof item === 'object' ? item.id : item) === productId);
      if (index >= 0) items.splice(index, 1);
      else {
        const product = parseJson(card.querySelector('[data-ppc-product-json]')) || {};
        items.push({ id: productId, handle: card.closest('[data-product-handle]')?.dataset.productHandle || '', title: product.title || 'Product', url: product.url || '#', image: product.featuredImage || '', price: product.variants?.[0]?.priceFormatted || '' });
      }
      setWishlist(items);
    }

    syncWishlist(items) {
      const normalized = Array.isArray(items) ? items.map(String) : [];
      this.root.querySelectorAll('[data-product-id]').forEach((slide) => {
        const button = slide.querySelector('[data-ppc-favorite]');
        if (!button) return;
        const active = normalized.includes(String(slide.dataset.productId));
        const title = slide.querySelector('.pianzi-product-v4__product-title')?.textContent.trim() || 'product';
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'} ${title} ${active ? 'from' : 'to'} wishlist`);
      });
    }

    handleQuickAdd(card, button) {
      if (button.disabled || button.classList.contains('is-loading')) return;
      const product = parseJson(card.querySelector('[data-ppc-product-json]'));
      if (!product?.variants?.length) return;

      const availableVariants = product.variants.filter((variant) => variant.available);
      if (!availableVariants.length) return;

      if (product.hasOnlyDefaultVariant || product.variants.length === 1) {
        this.addVariant(availableVariants[0].id, button, false);
      } else {
        const activeSwatchId = Number(card.querySelector('[data-ppc-swatch].is-active')?.dataset.variantId || 0);
        this.openModal(card, product, button, activeSwatchId);
      }
    }

    openModal(card, product, trigger, preferredVariantId = 0) {
      if (!this.modal) return;

      // Escape the section stacking context. Without this, later sections can paint
      // over the fixed panel on some Shopify layouts, especially while scrolling.
      if (this.modal.parentElement !== document.body) {
        const rootStyles = getComputedStyle(this.root);
        this.modal.style.setProperty('--ppc-quick-bg', rootStyles.getPropertyValue('--ppc-quick-bg'));
        this.modal.style.setProperty('--ppc-quick-color', rootStyles.getPropertyValue('--ppc-quick-color'));
        document.body.appendChild(this.modal);
      }

      this.activeCard = card;
      this.activeProduct = product;
      this.lastFocusedElement = trigger || document.activeElement;
      this.activeVariant = product.variants.find(
        (variant) => Number(variant.id) === Number(preferredVariantId) && variant.available
      ) || product.variants.find((variant) => variant.available) || product.variants[0];
      this.selectedOptions = [...this.activeVariant.options];
      this.modalTitle.textContent = product.title || '';
      this.modalMessage.textContent = '';
      this.renderModalOptions();
      this.updateModalState();

      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pianzi-product-v4-modal-open');
      this.isModalOpen = true;
      this.stopAutoplay();

      requestAnimationFrame(() => {
        this.modal.querySelector('button:not([disabled])')?.focus();
      });
    }

    closeModal() {
      if (!this.modal || !this.isModalOpen) return;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('pianzi-product-v4-modal-open');
      this.isModalOpen = false;
      this.activeCard = null;
      this.activeProduct = null;
      this.activeVariant = null;
      this.selectedOptions = [];
      this.modalMessage.textContent = '';
      this.lastFocusedElement?.focus?.();
      this.lastFocusedElement = null;
      this.startAutoplay();
    }

    renderModalOptions() {
      if (!this.modalOptions || !this.activeProduct) return;
      this.modalOptions.innerHTML = '';

      this.activeProduct.options.forEach((optionName, optionIndex) => {
        const group = document.createElement('div');
        group.className = 'pianzi-product-v4__modal-option';

        const title = document.createElement('p');
        title.className = 'pianzi-product-v4__modal-option-title';
        title.textContent = optionName;
        group.appendChild(title);

        const values = document.createElement('div');
        values.className = 'pianzi-product-v4__modal-values';

        const optionValues = unique(this.activeProduct.variants.map((variant) => variant.options[optionIndex]));
        optionValues.forEach((value) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'pianzi-product-v4__modal-value';
          button.dataset.optionIndex = String(optionIndex);
          button.dataset.optionValue = value;
          button.classList.toggle('is-selected', this.selectedOptions[optionIndex] === value);
          button.setAttribute('aria-pressed', String(this.selectedOptions[optionIndex] === value));

          const swatchColor = this.activeProduct.swatches?.[optionName]?.[value];
          if (swatchColor) {
            const chip = document.createElement('span');
            chip.className = 'pianzi-product-v4__modal-swatch';
            chip.style.setProperty('--ppc-modal-swatch', swatchColor);
            button.appendChild(chip);
          }

          const label = document.createElement('span');
          label.textContent = value;
          button.appendChild(label);

          const possible = this.activeProduct.variants.some(
            (variant) => variant.available && variant.options[optionIndex] === value &&
              variant.options.every((optionValue, index) => index === optionIndex || optionValue === this.selectedOptions[index])
          );
          button.disabled = !possible;

          button.addEventListener(
            'click',
            () => {
              if (button.disabled) return;
              this.selectedOptions[optionIndex] = value;
              const exactVariant = this.findVariant(this.selectedOptions);
              this.activeVariant = exactVariant;
              this.renderModalOptions();
              this.updateModalState();
            },
            { signal: this.signal }
          );

          values.appendChild(button);
        });

        group.appendChild(values);
        this.modalOptions.appendChild(group);
      });
    }

    findVariant(options) {
      if (!this.activeProduct) return null;
      return this.activeProduct.variants.find(
        (variant) => variant.options.every((option, index) => option === options[index])
      ) || null;
    }

    updateModalState() {
      if (!this.activeProduct) return;
      this.activeVariant = this.findVariant(this.selectedOptions) || this.activeVariant;
      const variant = this.activeVariant;

      this.modalPrice.textContent = variant?.priceFormatted || '';
      this.modalAdd.disabled = !variant?.available;
      this.modalAddLabel.textContent = variant?.available ? this.modalDefaultAddLabel : 'Unavailable';

      const image = variant?.image || this.activeProduct.featuredImage;
      if (image && this.modalImage && this.modalMedia) {
        this.modalImage.src = image;
        this.modalImage.alt = this.activeProduct.title || '';
        this.modalMedia.hidden = false;
      } else if (this.modalMedia) {
        this.modalMedia.hidden = true;
      }
    }

    async addVariant(variantId, button, closeAfter) {
      if (!variantId || !button || button.classList.contains('is-loading')) return;

      const label = button.querySelector('[data-ppc-quick-label], [data-ppc-modal-add-label]');
      const originalLabel = label?.textContent || '';
      button.classList.add('is-loading');
      button.disabled = true;
      this.modalMessage.textContent = '';

      const drawer = document.querySelector('cart-drawer');
      drawer?.setActiveElement?.(button);

      try {
        const response = await fetch(`${shopRoot()}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname
          })
        });

        const state = await response.json();
        if (!response.ok || state.status || state.errors) {
          throw new Error(state.description || state.message || state.errors || 'This option could not be added.');
        }

        if (state.sections && drawer?.renderContents) {
          drawer.renderContents(state);
        } else if (drawer?.open) {
          drawer.open(button);
        }

        this.dispatchCartEvents(state, variantId);
        this.announce('Added to bag');

        if (label) label.textContent = 'Added';
        if (closeAfter) {
          window.setTimeout(() => this.closeModal(), 260);
        } else {
          window.setTimeout(() => {
            if (label) label.textContent = originalLabel;
          }, 1200);
        }
      } catch (error) {
        console.error('PIANZI product carousel add failed:', error);
        const message = error.message || 'This option could not be added. Please try again.';
        if (this.isModalOpen) this.modalMessage.textContent = message;
        this.announce(message);
        if (label) label.textContent = originalLabel;
      } finally {
        button.classList.remove('is-loading');
        button.disabled = closeAfter ? !this.activeVariant?.available : false;
      }
    }

    dispatchCartEvents(state, variantId) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: state } }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: state } }));
      document.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { cart: state, variantId } }));

      try {
        const publisher = typeof publish === 'function' ? publish : window.publish;
        const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;
        if (typeof publisher === 'function' && events?.cartUpdate) {
          publisher(events.cartUpdate, {
            source: 'pianzi-product-carousel-v4',
            productVariantId: variantId,
            cartData: state
          });
        }
      } catch (error) {
        console.warn('PIANZI cart event could not be published:', error);
      }
    }

    trapModalFocus(event) {
      const focusable = Array.from(
        this.modalPanel?.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      ).filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    announce(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      window.setTimeout(() => { this.liveRegion.textContent = message; }, 20);
    }

    selectBlock(blockId) {
      const slide = this.slides.find((item) => item.dataset.blockId === blockId);
      if (!slide) return;
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const target = clamp(slide.offsetLeft - this.track.offsetLeft, 0, maxScroll);
      this.viewport.scrollTo({ left: target, behavior: 'smooth' });
      this.stopAutoplay();
    }

    destroy() {
      this.stopAutoplay();
      window.clearTimeout(this.resizeTimer);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.abortController.abort();
      this.closeModal();
      delete this.root.pianziProductCarouselV4;
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (!root.pianziProductCarouselV4) new PianziProductCarouselV4(root);
    });
  };

  window.PianziProductCarouselV4 = { init, Class: PianziProductCarouselV4 };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector?.(ROOT_SELECTOR);
    root?.pianziProductCarouselV4?.destroy();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.(ROOT_SELECTOR);
    root?.pianziProductCarouselV4?.selectBlock(event.detail?.blockId);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest?.(ROOT_SELECTOR);
    root?.pianziProductCarouselV4?.startAutoplay();
  });
})();
